import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Bell, Check, X, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

export default function NotificationsInbox() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const unread = items.filter((n) => !n.read).length;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (e) { console.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const key = (e) => { if (e.key === "Escape") { setOpen(false); wrapRef.current?.querySelector("button")?.focus(); } };
    if (open) { document.addEventListener("mousedown", handler); document.addEventListener("keydown", key); }
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("keydown", key); };
  }, [open]);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch {}
  };
  const markAll = async () => {
    for (const n of items.filter((x) => !x.read)) {
      // eslint-disable-next-line no-await-in-loop
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    load();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        className="relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 hover:bg-slate-100 text-slate-600"
        data-testid="notifications-btn"
        aria-label={unread ? `التنبيهات، ${unread} غير مقروءة` : "التنبيهات"}
        aria-expanded={open}
        aria-controls="notifications-panel"
        aria-haspopup="dialog"
      >
        <Bell className="h-5 w-5" strokeWidth={1.75} />
        {unread > 0 && (
          <span aria-hidden="true" className="absolute -top-0.5 -end-0.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center arabic-numerals">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div id="notifications-panel" role="dialog" aria-label="قائمة التنبيهات" className="absolute top-full end-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-cream-200 card-shadow-lg overflow-hidden z-50" data-testid="notifications-panel">
          <div className="p-4 border-b border-cream-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-teal-700" />
              <div className="font-heading font-bold text-slate-900 text-sm">التنبيهات</div>
            </div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-teal-700 font-semibold hover:underline">
                تعليم الكل كمقروء
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">جاري التحميل...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <div className="text-sm text-slate-500">لا توجد تنبيهات</div>
              </div>
            ) : (
              items.slice(0, 20).map((n) => (
                <NotifItem key={n.id} n={n} onRead={() => markRead(n.id)} onNav={() => setOpen(false)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifItem({ n, onRead, onNav }) {
  const dot = !n.read;
  const destination = n.resolved_link || n.link;
  const Wrapper = ({ children }) => destination
    ? <Link to={destination} onClick={() => { onRead(); onNav(); }} className="block">{children}</Link>
    : <div>{children}</div>;

  return (
    <Wrapper>
      <div className={`p-4 border-b border-cream-100 hover:bg-cream-50 cursor-pointer transition-colors ${dot ? "bg-cream-50/60" : ""}`} data-testid={`notif-${n.id}`}>
        <div className="flex items-start gap-3">
          {dot && <span className="mt-1.5 h-2 w-2 rounded-full bg-amber-500 shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 text-sm">{n.title}</div>
            <div className="text-xs text-slate-600 mt-1 line-clamp-2 leading-relaxed">{n.body}</div>
            <div className="text-[10px] text-slate-400 mt-2">
              {new Date(n.created_at).toLocaleString("ar-SA")}
            </div>
          </div>
          {!n.read && (
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRead(); }} className="grid min-h-11 min-w-11 place-items-center rounded text-slate-500 hover:text-teal-700" aria-label={`تعليم تنبيه ${n.title} كمقروء`}>
              <Check className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
